import type * as Types from '@hotelplan/supergraph-api';

import type { DocumentNode } from 'graphql';
import { HeroMediaImageFragmentDoc } from 'schemas/fragment/hero-media-gallery/fdr-hero-media-gallery.generated';
import { FdrPageComponentsFragmentDoc } from 'schemas/fragment/page-components/fdr-page-components.generated';
import {
  MediaImageFragmentDoc,
  MediaVideoFragmentDoc,
  MediaAudioFragmentDoc,
  MediaPdfFragmentDoc,
  MediaExternalFragmentDoc,
} from 'schemas/fragment/media/fdr-media.generated';
import { FdrDisrupterFragmentDoc } from 'schemas/fragment/disrupter/fdr-disrupter.generated';
import { FdrB2BInfoGroupsFragmentDoc } from 'schemas/fragment/b2b-info-groups/fdr-b2b-info-groups.generated';
import { FdrProductWebMetaFragmentDoc } from 'schemas/fragment/web-meta/fdr-product-web-meta.generated';
import { FdrRequestButtonFragmentDoc } from 'schemas/fragment/request-button/fdr-request-button.generated';
import { FdrBoosterFragmentDoc } from 'schemas/fragment/booster/fdr-booster.generated';
export type FdrHotelFragment = {
  __typename?: 'FdrHotel';
  leadText?: string | null;
  id: string;
  hero?: {
    __typename?: 'FdrImage';
    alt: string;
    description?: string | null;
    copyright?: string | null;
    rawUrl: string;
    resized: Array<{
      __typename?: 'FdrResizedImage';
      id: string;
      link: {
        __typename?: 'FdrLink';
        openMethod?: Types.FdrLinkOpenMethod | null;
        legacyUrl?: string | null;
        type: Types.FdrLinkType;
        url: string;
      };
    }>;
  } | null;
  pageComponents: {
    __typename?: 'FdrPageComponents';
    components?: Array<
      | { __typename: 'FdrAccordion' }
      | { __typename: 'FdrAccordionComponent' }
      | { __typename: 'FdrAgencyContactModule' }
      | {
          __typename: 'FdrAgencyRecommender';
          id: string;
          title?: string | null;
          bgColor?: string | null;
          fgColor?: string | null;
          agencies: {
            __typename?: 'FdrAgencyRecommenderAgencies';
            agenciesPage?: Array<{
              __typename?: 'FdrAgency';
              name: string;
              openStatus: Types.FdrAgencyOpenStatus;
              address?: {
                __typename?: 'FdrAgencyAddress';
                zip?: string | null;
                city?: string | null;
                region?: string | null;
                street?: string | null;
                country?: string | null;
              } | null;
              image?: {
                __typename?: 'FdrImage';
                resized: Array<{
                  __typename?: 'FdrResizedImage';
                  id: string;
                  link: {
                    __typename?: 'FdrLink';
                    openMethod?: Types.FdrLinkOpenMethod | null;
                    legacyUrl?: string | null;
                    type: Types.FdrLinkType;
                    url: string;
                  };
                }>;
              } | null;
              webMeta: {
                __typename?: 'FdrMeta';
                link: {
                  __typename?: 'FdrLink';
                  openMethod?: Types.FdrLinkOpenMethod | null;
                  legacyUrl?: string | null;
                  type: Types.FdrLinkType;
                  url: string;
                };
              };
            }> | null;
            pagination: {
              __typename?: 'FdrPagination';
              total: number;
              page: number;
              perPage: number;
            };
          };
        }
      | {
          __typename: 'FdrBenefitsComponent';
          id: string;
          text?: string | null;
          buttonLabel: string;
          buttonLink: {
            __typename?: 'FdrLink';
            openMethod?: Types.FdrLinkOpenMethod | null;
            legacyUrl?: string | null;
            type: Types.FdrLinkType;
            url: string;
          };
          benefitItems: Array<{ __typename?: 'FdrBenefitItem'; text: string }>;
        }
      | {
          __typename: 'FdrBlogArticleRecommender';
          id: string;
          title?: string | null;
          blogArticleItems: {
            __typename?: 'FdrAllBlogArticlesResponse';
            blogArticles?: Array<{
              __typename?: 'FdrBlogArticle';
              publicId: string;
              title?: string | null;
              text?: string | null;
              start?: any | null;
              flagline?: string | null;
              webMeta: {
                __typename?: 'FdrMeta';
                title: string;
                description: string;
                index: boolean;
                noFollow: boolean;
                sitemap: boolean;
                link: {
                  __typename?: 'FdrLink';
                  openMethod?: Types.FdrLinkOpenMethod | null;
                  legacyUrl?: string | null;
                  type: Types.FdrLinkType;
                  url: string;
                };
              };
              hero?: {
                __typename?: 'FdrImage';
                resized: Array<{
                  __typename?: 'FdrResizedImage';
                  id: string;
                  link: {
                    __typename?: 'FdrLink';
                    openMethod?: Types.FdrLinkOpenMethod | null;
                    legacyUrl?: string | null;
                    type: Types.FdrLinkType;
                    url: string;
                  };
                }>;
              } | null;
              employee?: {
                __typename?: 'FdrEmployee';
                name: string;
                image?: {
                  __typename?: 'FdrImage';
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                } | null;
              } | null;
            }> | null;
            pagination: {
              __typename?: 'FdrPagination';
              total: number;
              page: number;
              perPage: number;
            };
          };
        }
      | { __typename: 'FdrBrandsComponent' }
      | {
          __typename: 'FdrContactModule';
          id: string;
          flagline?: string | null;
          title?: string | null;
          moduleItems: Array<
            | {
                __typename?: 'FdrContactModuleLink';
                title?: string | null;
                link: {
                  __typename?: 'FdrLink';
                  openMethod?: Types.FdrLinkOpenMethod | null;
                  legacyUrl?: string | null;
                  type: Types.FdrLinkType;
                  url: string;
                };
                icon?: {
                  __typename?: 'FdrImage';
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                } | null;
              }
            | {
                __typename?: 'FdrContactModulePhone';
                phone?: string | null;
                title?: string | null;
                icon?: {
                  __typename?: 'FdrImage';
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                } | null;
              }
          >;
        }
      | { __typename: 'FdrEmployeesComponent' }
      | {
          __typename: 'FdrFaq';
          bgColor?: string | null;
          fgColor?: string | null;
          answer: string;
          question: string;
        }
      | { __typename: 'FdrFlightOfferRecommender' }
      | { __typename: 'FdrFlightRecommender' }
      | { __typename: 'FdrGeoChildrenPlaceholder' }
      | { __typename: 'FdrGeoClimatePlaceholder' }
      | { __typename: 'FdrGeoGalleryPlaceholder' }
      | { __typename: 'FdrGeoLeadTextPlaceholder' }
      | { __typename: 'FdrGeoLongTextPlaceholder' }
      | { __typename: 'FdrGeoMapPlaceholder' }
      | { __typename: 'FdrGeoOfferButtonPlaceholder' }
      | { __typename: 'FdrGeoOfferRecommender' }
      | {
          __typename: 'FdrGeoRecommender';
          id: string;
          title?: string | null;
          fgColor?: string | null;
          bgColor?: string | null;
          itemsPage: {
            __typename?: 'FdrGeoRecommenderItem';
            pagination: {
              __typename?: 'FdrPagination';
              total: number;
              page: number;
              perPage: number;
            };
            items?: Array<
              | {
                  __typename: 'FdrContinent';
                  id: string;
                  title: string;
                  webMeta: {
                    __typename?: 'FdrGeoWebMeta';
                    descriptionWebMeta?: {
                      __typename?: 'FdrMeta';
                      link: {
                        __typename?: 'FdrLink';
                        openMethod?: Types.FdrLinkOpenMethod | null;
                        legacyUrl?: string | null;
                        type: Types.FdrLinkType;
                        url: string;
                      };
                    } | null;
                  };
                  image?: {
                    __typename?: 'FdrImage';
                    alt: string;
                    description?: string | null;
                    copyright?: string | null;
                    rawUrl: string;
                    resized: Array<{
                      __typename?: 'FdrResizedImage';
                      id: string;
                      link: {
                        __typename?: 'FdrLink';
                        openMethod?: Types.FdrLinkOpenMethod | null;
                        legacyUrl?: string | null;
                        type: Types.FdrLinkType;
                        url: string;
                      };
                    }>;
                  } | null;
                }
              | {
                  __typename: 'FdrCountry';
                  id: string;
                  title: string;
                  webMeta: {
                    __typename?: 'FdrGeoWebMeta';
                    descriptionWebMeta?: {
                      __typename?: 'FdrMeta';
                      link: {
                        __typename?: 'FdrLink';
                        openMethod?: Types.FdrLinkOpenMethod | null;
                        legacyUrl?: string | null;
                        type: Types.FdrLinkType;
                        url: string;
                      };
                    } | null;
                  };
                  image?: {
                    __typename?: 'FdrImage';
                    alt: string;
                    description?: string | null;
                    copyright?: string | null;
                    rawUrl: string;
                    resized: Array<{
                      __typename?: 'FdrResizedImage';
                      id: string;
                      link: {
                        __typename?: 'FdrLink';
                        openMethod?: Types.FdrLinkOpenMethod | null;
                        legacyUrl?: string | null;
                        type: Types.FdrLinkType;
                        url: string;
                      };
                    }>;
                  } | null;
                }
              | {
                  __typename: 'FdrCountryGroup';
                  id: string;
                  title: string;
                  webMeta: {
                    __typename?: 'FdrGeoWebMeta';
                    descriptionWebMeta?: {
                      __typename?: 'FdrMeta';
                      link: {
                        __typename?: 'FdrLink';
                        openMethod?: Types.FdrLinkOpenMethod | null;
                        legacyUrl?: string | null;
                        type: Types.FdrLinkType;
                        url: string;
                      };
                    } | null;
                  };
                  image?: {
                    __typename?: 'FdrImage';
                    alt: string;
                    description?: string | null;
                    copyright?: string | null;
                    rawUrl: string;
                    resized: Array<{
                      __typename?: 'FdrResizedImage';
                      id: string;
                      link: {
                        __typename?: 'FdrLink';
                        openMethod?: Types.FdrLinkOpenMethod | null;
                        legacyUrl?: string | null;
                        type: Types.FdrLinkType;
                        url: string;
                      };
                    }>;
                  } | null;
                }
              | {
                  __typename: 'FdrDestination';
                  id: string;
                  title: string;
                  webMeta: {
                    __typename?: 'FdrGeoWebMeta';
                    descriptionWebMeta?: {
                      __typename?: 'FdrMeta';
                      link: {
                        __typename?: 'FdrLink';
                        openMethod?: Types.FdrLinkOpenMethod | null;
                        legacyUrl?: string | null;
                        type: Types.FdrLinkType;
                        url: string;
                      };
                    } | null;
                  };
                  image?: {
                    __typename?: 'FdrImage';
                    alt: string;
                    description?: string | null;
                    copyright?: string | null;
                    rawUrl: string;
                    resized: Array<{
                      __typename?: 'FdrResizedImage';
                      id: string;
                      link: {
                        __typename?: 'FdrLink';
                        openMethod?: Types.FdrLinkOpenMethod | null;
                        legacyUrl?: string | null;
                        type: Types.FdrLinkType;
                        url: string;
                      };
                    }>;
                  } | null;
                }
              | {
                  __typename: 'FdrRegion';
                  id: string;
                  title: string;
                  webMeta: {
                    __typename?: 'FdrGeoWebMeta';
                    descriptionWebMeta?: {
                      __typename?: 'FdrMeta';
                      link: {
                        __typename?: 'FdrLink';
                        openMethod?: Types.FdrLinkOpenMethod | null;
                        legacyUrl?: string | null;
                        type: Types.FdrLinkType;
                        url: string;
                      };
                    } | null;
                  };
                  image?: {
                    __typename?: 'FdrImage';
                    alt: string;
                    description?: string | null;
                    copyright?: string | null;
                    rawUrl: string;
                    resized: Array<{
                      __typename?: 'FdrResizedImage';
                      id: string;
                      link: {
                        __typename?: 'FdrLink';
                        openMethod?: Types.FdrLinkOpenMethod | null;
                        legacyUrl?: string | null;
                        type: Types.FdrLinkType;
                        url: string;
                      };
                    }>;
                  } | null;
                }
              | {
                  __typename: 'FdrResort';
                  id: string;
                  title: string;
                  image?: {
                    __typename?: 'FdrImage';
                    alt: string;
                    description?: string | null;
                    copyright?: string | null;
                    rawUrl: string;
                    resized: Array<{
                      __typename?: 'FdrResizedImage';
                      id: string;
                      link: {
                        __typename?: 'FdrLink';
                        openMethod?: Types.FdrLinkOpenMethod | null;
                        legacyUrl?: string | null;
                        type: Types.FdrLinkType;
                        url: string;
                      };
                    }>;
                  } | null;
                }
            > | null;
          };
        }
      | { __typename: 'FdrGeoRecommenderPlaceholder' }
      | {
          __typename: 'FdrHeroMediaGallery';
          title?: string | null;
          titleColor?: string | null;
          flagline?: string | null;
          media: Array<
            | { __typename: 'FdrAudio' }
            | { __typename: 'FdrExternalMedia' }
            | {
                __typename: 'FdrImage';
                alt: string;
                description?: string | null;
                copyright?: string | null;
                rawUrl: string;
                resized: Array<{
                  __typename?: 'FdrResizedImage';
                  id: string;
                  link: {
                    __typename?: 'FdrLink';
                    openMethod?: Types.FdrLinkOpenMethod | null;
                    legacyUrl?: string | null;
                    type: Types.FdrLinkType;
                    url: string;
                  };
                }>;
              }
            | { __typename: 'FdrPdf' }
            | {
                __typename: 'FdrVideo';
                copyright?: string | null;
                width?: number | null;
                height?: number | null;
                preview?: {
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  mediaPreviewResized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                } | null;
                resourceLink: {
                  __typename?: 'FdrLink';
                  openMethod?: Types.FdrLinkOpenMethod | null;
                  legacyUrl?: string | null;
                  type: Types.FdrLinkType;
                  url: string;
                };
              }
          >;
        }
      | {
          __typename: 'FdrImageText';
          title?: string | null;
          text?: string | null;
          bgColor?: string | null;
          fgColor?: string | null;
          width?: string | null;
          topPadding?: string | null;
          bottomPadding?: string | null;
          placement?: string | null;
          image?: {
            __typename?: 'FdrImage';
            alt: string;
            description?: string | null;
            copyright?: string | null;
            rawUrl: string;
            imageTextResized: Array<{
              __typename?: 'FdrResizedImage';
              id: string;
              link: {
                __typename?: 'FdrLink';
                openMethod?: Types.FdrLinkOpenMethod | null;
                legacyUrl?: string | null;
                type: Types.FdrLinkType;
                url: string;
              };
            }>;
            imageTextResizedLarge: Array<{
              __typename?: 'FdrResizedImage';
              id: string;
              link: {
                __typename?: 'FdrLink';
                openMethod?: Types.FdrLinkOpenMethod | null;
                legacyUrl?: string | null;
                type: Types.FdrLinkType;
                url: string;
              };
            }>;
          } | null;
        }
      | { __typename: 'FdrLinkList' }
      | {
          __typename: 'FdrMarkdownText';
          id: string;
          bgColor?: string | null;
          fgColor?: string | null;
          text?: string | null;
          topPadding?: string | null;
          bottomPadding?: string | null;
          width?: string | null;
          bgImage?: {
            __typename?: 'FdrImage';
            alt: string;
            description?: string | null;
            copyright?: string | null;
            rawUrl: string;
            resized: Array<{
              __typename?: 'FdrResizedImage';
              id: string;
              link: {
                __typename?: 'FdrLink';
                openMethod?: Types.FdrLinkOpenMethod | null;
                legacyUrl?: string | null;
                type: Types.FdrLinkType;
                url: string;
              };
            }>;
          } | null;
        }
      | {
          __typename: 'FdrMarketingRecommender';
          id: string;
          fgColor?: string | null;
          titleFgColor?: string | null;
          bgColor?: string | null;
          title?: string | null;
          displayType: string;
          marketingItems: Array<{
            __typename?: 'FdrMarketingRecommenderItem';
            title?: string | null;
            subtitle: string;
            link: {
              __typename?: 'FdrLink';
              openMethod?: Types.FdrLinkOpenMethod | null;
              legacyUrl?: string | null;
              type: Types.FdrLinkType;
              url: string;
            };
            media?:
              | {
                  __typename?: 'FdrAudio';
                  resourceLink: {
                    __typename?: 'FdrLink';
                    openMethod?: Types.FdrLinkOpenMethod | null;
                    legacyUrl?: string | null;
                    type: Types.FdrLinkType;
                    url: string;
                  };
                  preview?: {
                    __typename?: 'FdrImage';
                    alt: string;
                    description?: string | null;
                    copyright?: string | null;
                    rawUrl: string;
                    resized: Array<{
                      __typename?: 'FdrResizedImage';
                      id: string;
                      link: {
                        __typename?: 'FdrLink';
                        openMethod?: Types.FdrLinkOpenMethod | null;
                        legacyUrl?: string | null;
                        type: Types.FdrLinkType;
                        url: string;
                      };
                    }>;
                  } | null;
                  image?: {
                    __typename?: 'FdrImage';
                    alt: string;
                    description?: string | null;
                    copyright?: string | null;
                    rawUrl: string;
                    resized: Array<{
                      __typename?: 'FdrResizedImage';
                      id: string;
                      link: {
                        __typename?: 'FdrLink';
                        openMethod?: Types.FdrLinkOpenMethod | null;
                        legacyUrl?: string | null;
                        type: Types.FdrLinkType;
                        url: string;
                      };
                    }>;
                  } | null;
                }
              | {
                  __typename?: 'FdrExternalMedia';
                  resourceLink: {
                    __typename?: 'FdrLink';
                    openMethod?: Types.FdrLinkOpenMethod | null;
                    legacyUrl?: string | null;
                    type: Types.FdrLinkType;
                    url: string;
                  };
                  preview?: {
                    __typename?: 'FdrImage';
                    alt: string;
                    description?: string | null;
                    copyright?: string | null;
                    rawUrl: string;
                    resized: Array<{
                      __typename?: 'FdrResizedImage';
                      id: string;
                      link: {
                        __typename?: 'FdrLink';
                        openMethod?: Types.FdrLinkOpenMethod | null;
                        legacyUrl?: string | null;
                        type: Types.FdrLinkType;
                        url: string;
                      };
                    }>;
                  } | null;
                  image?: {
                    __typename?: 'FdrImage';
                    alt: string;
                    description?: string | null;
                    copyright?: string | null;
                    rawUrl: string;
                    resized: Array<{
                      __typename?: 'FdrResizedImage';
                      id: string;
                      link: {
                        __typename?: 'FdrLink';
                        openMethod?: Types.FdrLinkOpenMethod | null;
                        legacyUrl?: string | null;
                        type: Types.FdrLinkType;
                        url: string;
                      };
                    }>;
                  } | null;
                }
              | {
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  resizedPreview: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                  resizedImage: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                }
              | {
                  __typename?: 'FdrPdf';
                  resourceLink: {
                    __typename?: 'FdrLink';
                    openMethod?: Types.FdrLinkOpenMethod | null;
                    legacyUrl?: string | null;
                    type: Types.FdrLinkType;
                    url: string;
                  };
                  preview?: {
                    __typename?: 'FdrImage';
                    alt: string;
                    description?: string | null;
                    copyright?: string | null;
                    rawUrl: string;
                    resized: Array<{
                      __typename?: 'FdrResizedImage';
                      id: string;
                      link: {
                        __typename?: 'FdrLink';
                        openMethod?: Types.FdrLinkOpenMethod | null;
                        legacyUrl?: string | null;
                        type: Types.FdrLinkType;
                        url: string;
                      };
                    }>;
                  } | null;
                  image?: {
                    __typename?: 'FdrImage';
                    alt: string;
                    description?: string | null;
                    copyright?: string | null;
                    rawUrl: string;
                    resized: Array<{
                      __typename?: 'FdrResizedImage';
                      id: string;
                      link: {
                        __typename?: 'FdrLink';
                        openMethod?: Types.FdrLinkOpenMethod | null;
                        legacyUrl?: string | null;
                        type: Types.FdrLinkType;
                        url: string;
                      };
                    }>;
                  } | null;
                }
              | {
                  __typename?: 'FdrVideo';
                  resourceLink: {
                    __typename?: 'FdrLink';
                    openMethod?: Types.FdrLinkOpenMethod | null;
                    legacyUrl?: string | null;
                    type: Types.FdrLinkType;
                    url: string;
                  };
                  preview?: {
                    __typename?: 'FdrImage';
                    alt: string;
                    description?: string | null;
                    copyright?: string | null;
                    rawUrl: string;
                    resized: Array<{
                      __typename?: 'FdrResizedImage';
                      id: string;
                      link: {
                        __typename?: 'FdrLink';
                        openMethod?: Types.FdrLinkOpenMethod | null;
                        legacyUrl?: string | null;
                        type: Types.FdrLinkType;
                        url: string;
                      };
                    }>;
                  } | null;
                  image?: {
                    __typename?: 'FdrImage';
                    alt: string;
                    description?: string | null;
                    copyright?: string | null;
                    rawUrl: string;
                    resized: Array<{
                      __typename?: 'FdrResizedImage';
                      id: string;
                      link: {
                        __typename?: 'FdrLink';
                        openMethod?: Types.FdrLinkOpenMethod | null;
                        legacyUrl?: string | null;
                        type: Types.FdrLinkType;
                        url: string;
                      };
                    }>;
                  } | null;
                }
              | null;
          }>;
        }
      | { __typename: 'FdrMarketingTeaser' }
      | {
          __typename: 'FdrMediaGallery';
          bgColor?: string | null;
          fgColor?: string | null;
          media: Array<
            | {
                __typename: 'FdrAudio';
                title?: string | null;
                preview?: {
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  mediaPreviewResized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                } | null;
                resourceLink: {
                  __typename?: 'FdrLink';
                  openMethod?: Types.FdrLinkOpenMethod | null;
                  legacyUrl?: string | null;
                  type: Types.FdrLinkType;
                  url: string;
                };
              }
            | {
                __typename: 'FdrExternalMedia';
                title?: string | null;
                preview?: {
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  mediaPreviewResized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                } | null;
                resourceLink: {
                  __typename?: 'FdrLink';
                  openMethod?: Types.FdrLinkOpenMethod | null;
                  legacyUrl?: string | null;
                  type: Types.FdrLinkType;
                  url: string;
                };
              }
            | {
                __typename: 'FdrImage';
                alt: string;
                description?: string | null;
                copyright?: string | null;
                rawUrl: string;
                mediaImageResized: Array<{
                  __typename?: 'FdrResizedImage';
                  id: string;
                  link: {
                    __typename?: 'FdrLink';
                    openMethod?: Types.FdrLinkOpenMethod | null;
                    legacyUrl?: string | null;
                    type: Types.FdrLinkType;
                    url: string;
                  };
                }>;
                mediaImagePreviewResized: Array<{
                  __typename?: 'FdrResizedImage';
                  id: string;
                  link: {
                    __typename?: 'FdrLink';
                    openMethod?: Types.FdrLinkOpenMethod | null;
                    legacyUrl?: string | null;
                    type: Types.FdrLinkType;
                    url: string;
                  };
                }>;
                coordinates?: {
                  __typename?: 'FdrCoordinates';
                  latitude: number;
                  longitude: number;
                } | null;
              }
            | {
                __typename: 'FdrPdf';
                caption: string;
                preview?: {
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  mediaPreviewResized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                } | null;
                link: {
                  __typename?: 'FdrLink';
                  openMethod?: Types.FdrLinkOpenMethod | null;
                  legacyUrl?: string | null;
                  type: Types.FdrLinkType;
                  url: string;
                };
              }
            | {
                __typename: 'FdrVideo';
                copyright?: string | null;
                width?: number | null;
                height?: number | null;
                preview?: {
                  __typename?: 'FdrImage';
                  alt: string;
                  description?: string | null;
                  copyright?: string | null;
                  rawUrl: string;
                  mediaPreviewResized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                } | null;
                resourceLink: {
                  __typename?: 'FdrLink';
                  openMethod?: Types.FdrLinkOpenMethod | null;
                  legacyUrl?: string | null;
                  type: Types.FdrLinkType;
                  url: string;
                };
              }
          >;
        }
      | { __typename: 'FdrNewsArticlesRecommender' }
      | { __typename: 'FdrNewsletterSignup' }
      | { __typename: 'FdrProductOfferRecommender' }
      | { __typename: 'FdrProductOffersMap' }
      | {
          __typename: 'FdrProductRecommender';
          id: string;
          title?: string | null;
          fgColor?: string | null;
          bgColor?: string | null;
          productPage: {
            __typename?: 'FdrProductRecommenderItems';
            products?: Array<
              | {
                  __typename: 'FdrAdventureTravel';
                  name: string;
                  teaser: string;
                  id: string;
                  bookmarked: boolean;
                  images?: Array<{
                    __typename?: 'FdrImage';
                    alt: string;
                    description?: string | null;
                    copyright?: string | null;
                    rawUrl: string;
                    resized: Array<{
                      __typename?: 'FdrResizedImage';
                      id: string;
                      link: {
                        __typename?: 'FdrLink';
                        openMethod?: Types.FdrLinkOpenMethod | null;
                        legacyUrl?: string | null;
                        type: Types.FdrLinkType;
                        url: string;
                      };
                    }>;
                  }> | null;
                  disrupters?: Array<{
                    __typename?: 'FdrDisrupter';
                    text: string;
                    bgColor?: string | null;
                    fgColor?: string | null;
                  }> | null;
                  route?: Array<{
                    __typename?: 'FdrAdventureTravelRoutePoint';
                    locationName: string;
                    coordinates: {
                      __typename?: 'FdrCoordinates';
                      latitude: number;
                      longitude: number;
                    };
                  }> | null;
                  webMeta: {
                    __typename?: 'FdrProductWebMeta';
                    descriptionWebMeta?: {
                      __typename?: 'FdrMeta';
                      link: {
                        __typename?: 'FdrLink';
                        openMethod?: Types.FdrLinkOpenMethod | null;
                        legacyUrl?: string | null;
                        type: Types.FdrLinkType;
                        url: string;
                      };
                    } | null;
                  };
                  featureGroups?: Array<{
                    __typename?: 'FdrProductFeatureGroup';
                    id: string;
                    name: string;
                    features: Array<{
                      __typename?: 'FdrProductFeature';
                      id: string;
                      name: string;
                      enabled: boolean;
                    }>;
                  }> | null;
                }
              | { __typename: 'FdrCruise' }
              | {
                  __typename: 'FdrHotel';
                  name: string;
                  id: string;
                  bookmarked: boolean;
                  images?: Array<{
                    __typename?: 'FdrImage';
                    alt: string;
                    description?: string | null;
                    copyright?: string | null;
                    rawUrl: string;
                    resized: Array<{
                      __typename?: 'FdrResizedImage';
                      id: string;
                      link: {
                        __typename?: 'FdrLink';
                        openMethod?: Types.FdrLinkOpenMethod | null;
                        legacyUrl?: string | null;
                        type: Types.FdrLinkType;
                        url: string;
                      };
                    }>;
                  }> | null;
                  disrupters?: Array<{
                    __typename?: 'FdrDisrupter';
                    text: string;
                    bgColor?: string | null;
                    fgColor?: string | null;
                  }> | null;
                  webMeta: {
                    __typename?: 'FdrProductWebMeta';
                    descriptionWebMeta?: {
                      __typename?: 'FdrMeta';
                      link: {
                        __typename?: 'FdrLink';
                        openMethod?: Types.FdrLinkOpenMethod | null;
                        legacyUrl?: string | null;
                        type: Types.FdrLinkType;
                        url: string;
                      };
                    } | null;
                  };
                }
              | {
                  __typename: 'FdrRoundTrip';
                  name: string;
                  mode?: string | null;
                  giataCode?: number | null;
                  leadText?: string | null;
                  duration: string;
                  id: string;
                  bookmarked: boolean;
                  images?: Array<{
                    __typename?: 'FdrImage';
                    alt: string;
                    description?: string | null;
                    copyright?: string | null;
                    rawUrl: string;
                    resized: Array<{
                      __typename?: 'FdrResizedImage';
                      id: string;
                      link: {
                        __typename?: 'FdrLink';
                        openMethod?: Types.FdrLinkOpenMethod | null;
                        legacyUrl?: string | null;
                        type: Types.FdrLinkType;
                        url: string;
                      };
                    }>;
                  }> | null;
                  disrupters?: Array<{
                    __typename?: 'FdrDisrupter';
                    text: string;
                    bgColor?: string | null;
                    fgColor?: string | null;
                  }> | null;
                  webMeta: {
                    __typename?: 'FdrProductWebMeta';
                    descriptionWebMeta?: {
                      __typename?: 'FdrMeta';
                      link: {
                        __typename?: 'FdrLink';
                        openMethod?: Types.FdrLinkOpenMethod | null;
                        legacyUrl?: string | null;
                        type: Types.FdrLinkType;
                        url: string;
                      };
                    } | null;
                  };
                  featureGroups?: Array<{
                    __typename?: 'FdrProductFeatureGroup';
                    id: string;
                    name: string;
                    features: Array<{
                      __typename?: 'FdrProductFeature';
                      id: string;
                      name: string;
                      enabled: boolean;
                    }>;
                  }> | null;
                }
            > | null;
            pagination: {
              __typename?: 'FdrPagination';
              total: number;
              page: number;
              perPage: number;
            };
          };
        }
      | {
          __typename: 'FdrRawText';
          id: string;
          bgColor?: string | null;
          fgColor?: string | null;
          text?: string | null;
          topPadding?: string | null;
          bottomPadding?: string | null;
          width?: string | null;
          bgImage?: {
            __typename?: 'FdrImage';
            alt: string;
            description?: string | null;
            copyright?: string | null;
            rawUrl: string;
            resized: Array<{
              __typename?: 'FdrResizedImage';
              id: string;
              link: {
                __typename?: 'FdrLink';
                openMethod?: Types.FdrLinkOpenMethod | null;
                legacyUrl?: string | null;
                type: Types.FdrLinkType;
                url: string;
              };
            }>;
          } | null;
        }
      | { __typename: 'FdrRssBlogArticleRecommender' }
      | { __typename: 'FdrSmartSeerRecommender' }
      | { __typename: 'FdrSrlButton' }
      | {
          __typename: 'FdrThemeGeoRecommender';
          id: string;
          title?: string | null;
          bgColor?: string | null;
          fgColor?: string | null;
          displayType: string;
          itemsPage: {
            __typename?: 'FdrThemeGeoRecommenderItems';
            pagination: {
              __typename?: 'FdrPagination';
              total: number;
              page: number;
              perPage: number;
            };
            items?: Array<{
              __typename?: 'FdrThemeGeoPage';
              title: string;
              image?: {
                __typename?: 'FdrImage';
                alt: string;
                description?: string | null;
                copyright?: string | null;
                rawUrl: string;
                resized: Array<{
                  __typename?: 'FdrResizedImage';
                  id: string;
                  link: {
                    __typename?: 'FdrLink';
                    openMethod?: Types.FdrLinkOpenMethod | null;
                    legacyUrl?: string | null;
                    type: Types.FdrLinkType;
                    url: string;
                  };
                }>;
              } | null;
              geo:
                | {
                    __typename: 'FdrContinent';
                    id: string;
                    title: string;
                    webMeta: {
                      __typename?: 'FdrGeoWebMeta';
                      descriptionWebMeta?: {
                        __typename?: 'FdrMeta';
                        link: {
                          __typename?: 'FdrLink';
                          openMethod?: Types.FdrLinkOpenMethod | null;
                          legacyUrl?: string | null;
                          type: Types.FdrLinkType;
                          url: string;
                        };
                      } | null;
                    };
                    image?: {
                      __typename?: 'FdrImage';
                      alt: string;
                      description?: string | null;
                      copyright?: string | null;
                      rawUrl: string;
                      resized: Array<{
                        __typename?: 'FdrResizedImage';
                        id: string;
                        link: {
                          __typename?: 'FdrLink';
                          openMethod?: Types.FdrLinkOpenMethod | null;
                          legacyUrl?: string | null;
                          type: Types.FdrLinkType;
                          url: string;
                        };
                      }>;
                    } | null;
                  }
                | {
                    __typename: 'FdrCountry';
                    id: string;
                    title: string;
                    webMeta: {
                      __typename?: 'FdrGeoWebMeta';
                      descriptionWebMeta?: {
                        __typename?: 'FdrMeta';
                        link: {
                          __typename?: 'FdrLink';
                          openMethod?: Types.FdrLinkOpenMethod | null;
                          legacyUrl?: string | null;
                          type: Types.FdrLinkType;
                          url: string;
                        };
                      } | null;
                    };
                    image?: {
                      __typename?: 'FdrImage';
                      alt: string;
                      description?: string | null;
                      copyright?: string | null;
                      rawUrl: string;
                      resized: Array<{
                        __typename?: 'FdrResizedImage';
                        id: string;
                        link: {
                          __typename?: 'FdrLink';
                          openMethod?: Types.FdrLinkOpenMethod | null;
                          legacyUrl?: string | null;
                          type: Types.FdrLinkType;
                          url: string;
                        };
                      }>;
                    } | null;
                  }
                | {
                    __typename: 'FdrCountryGroup';
                    id: string;
                    title: string;
                    webMeta: {
                      __typename?: 'FdrGeoWebMeta';
                      descriptionWebMeta?: {
                        __typename?: 'FdrMeta';
                        link: {
                          __typename?: 'FdrLink';
                          openMethod?: Types.FdrLinkOpenMethod | null;
                          legacyUrl?: string | null;
                          type: Types.FdrLinkType;
                          url: string;
                        };
                      } | null;
                    };
                    image?: {
                      __typename?: 'FdrImage';
                      alt: string;
                      description?: string | null;
                      copyright?: string | null;
                      rawUrl: string;
                      resized: Array<{
                        __typename?: 'FdrResizedImage';
                        id: string;
                        link: {
                          __typename?: 'FdrLink';
                          openMethod?: Types.FdrLinkOpenMethod | null;
                          legacyUrl?: string | null;
                          type: Types.FdrLinkType;
                          url: string;
                        };
                      }>;
                    } | null;
                  }
                | {
                    __typename: 'FdrDestination';
                    id: string;
                    title: string;
                    webMeta: {
                      __typename?: 'FdrGeoWebMeta';
                      descriptionWebMeta?: {
                        __typename?: 'FdrMeta';
                        link: {
                          __typename?: 'FdrLink';
                          openMethod?: Types.FdrLinkOpenMethod | null;
                          legacyUrl?: string | null;
                          type: Types.FdrLinkType;
                          url: string;
                        };
                      } | null;
                    };
                    image?: {
                      __typename?: 'FdrImage';
                      alt: string;
                      description?: string | null;
                      copyright?: string | null;
                      rawUrl: string;
                      resized: Array<{
                        __typename?: 'FdrResizedImage';
                        id: string;
                        link: {
                          __typename?: 'FdrLink';
                          openMethod?: Types.FdrLinkOpenMethod | null;
                          legacyUrl?: string | null;
                          type: Types.FdrLinkType;
                          url: string;
                        };
                      }>;
                    } | null;
                  }
                | {
                    __typename: 'FdrRegion';
                    id: string;
                    title: string;
                    webMeta: {
                      __typename?: 'FdrGeoWebMeta';
                      descriptionWebMeta?: {
                        __typename?: 'FdrMeta';
                        link: {
                          __typename?: 'FdrLink';
                          openMethod?: Types.FdrLinkOpenMethod | null;
                          legacyUrl?: string | null;
                          type: Types.FdrLinkType;
                          url: string;
                        };
                      } | null;
                    };
                    image?: {
                      __typename?: 'FdrImage';
                      alt: string;
                      description?: string | null;
                      copyright?: string | null;
                      rawUrl: string;
                      resized: Array<{
                        __typename?: 'FdrResizedImage';
                        id: string;
                        link: {
                          __typename?: 'FdrLink';
                          openMethod?: Types.FdrLinkOpenMethod | null;
                          legacyUrl?: string | null;
                          type: Types.FdrLinkType;
                          url: string;
                        };
                      }>;
                    } | null;
                  }
                | {
                    __typename: 'FdrResort';
                    id: string;
                    title: string;
                    image?: {
                      __typename?: 'FdrImage';
                      alt: string;
                      description?: string | null;
                      copyright?: string | null;
                      rawUrl: string;
                      resized: Array<{
                        __typename?: 'FdrResizedImage';
                        id: string;
                        link: {
                          __typename?: 'FdrLink';
                          openMethod?: Types.FdrLinkOpenMethod | null;
                          legacyUrl?: string | null;
                          type: Types.FdrLinkType;
                          url: string;
                        };
                      }>;
                    } | null;
                  };
              theme: {
                __typename?: 'FdrTheme';
                id: string;
                title: string;
                image?: {
                  __typename?: 'FdrImage';
                  resized: Array<{
                    __typename?: 'FdrResizedImage';
                    id: string;
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  }>;
                } | null;
                webMeta: {
                  __typename?: 'FdrThemeWebMeta';
                  descriptionWebMeta?: {
                    __typename?: 'FdrMeta';
                    link: {
                      __typename?: 'FdrLink';
                      openMethod?: Types.FdrLinkOpenMethod | null;
                      legacyUrl?: string | null;
                      type: Types.FdrLinkType;
                      url: string;
                    };
                  } | null;
                };
              };
              webMeta: {
                __typename?: 'FdrMeta';
                link: {
                  __typename?: 'FdrLink';
                  openMethod?: Types.FdrLinkOpenMethod | null;
                  legacyUrl?: string | null;
                  type: Types.FdrLinkType;
                  url: string;
                };
              };
            }> | null;
          };
        }
      | { __typename: 'FdrThemeOfferRecommender' }
      | {
          __typename: 'FdrThemeRecommender';
          id: string;
          title?: string | null;
          bgColor?: string | null;
          fgColor?: string | null;
          displayType: string;
          themePage: {
            __typename?: 'FdrThemeRecommenderPage';
            pagination: {
              __typename?: 'FdrPagination';
              total: number;
              page: number;
              perPage: number;
            };
            themes?: Array<{
              __typename?: 'FdrTheme';
              id: string;
              title: string;
              image?: {
                __typename?: 'FdrImage';
                resized: Array<{
                  __typename?: 'FdrResizedImage';
                  id: string;
                  link: {
                    __typename?: 'FdrLink';
                    openMethod?: Types.FdrLinkOpenMethod | null;
                    legacyUrl?: string | null;
                    type: Types.FdrLinkType;
                    url: string;
                  };
                }>;
              } | null;
              webMeta: {
                __typename?: 'FdrThemeWebMeta';
                descriptionWebMeta?: {
                  __typename?: 'FdrMeta';
                  link: {
                    __typename?: 'FdrLink';
                    openMethod?: Types.FdrLinkOpenMethod | null;
                    legacyUrl?: string | null;
                    type: Types.FdrLinkType;
                    url: string;
                  };
                } | null;
              };
            }> | null;
          };
        }
      | {
          __typename: 'FdrUspBoxesComponent';
          id: string;
          bgColor?: string | null;
          fgColor?: string | null;
          boxes: Array<{
            __typename?: 'FdrUspBox';
            title: string;
            text?: string | null;
            subtitle?: string | null;
            icon?: {
              __typename?: 'FdrImage';
              alt: string;
              description?: string | null;
              copyright?: string | null;
              rawUrl: string;
              resized: Array<{
                __typename?: 'FdrResizedImage';
                id: string;
                link: {
                  __typename?: 'FdrLink';
                  openMethod?: Types.FdrLinkOpenMethod | null;
                  legacyUrl?: string | null;
                  type: Types.FdrLinkType;
                  url: string;
                };
              }>;
            } | null;
          }>;
        }
    > | null;
    pagination: {
      __typename?: 'FdrPagination';
      total: number;
      page: number;
      perPage: number;
    };
  };
  b2bInformationGroups?: Array<{
    __typename?: 'FdrProductB2bInformationGroup';
    id: string;
    name?: string | null;
    textInformation?: Array<{
      __typename?: 'FdrTitleText';
      title: string;
      text: string;
    }> | null;
    featuresInformation?: Array<{
      __typename?: 'FdrProductFeatureGroup';
      id: string;
      name: string;
      features: Array<{
        __typename?: 'FdrProductFeature';
        id: string;
        name: string;
        enabled: boolean;
      }>;
    }> | null;
  }> | null;
  webMeta: {
    __typename?: 'FdrProductWebMeta';
    meta?: {
      __typename?: 'FdrMeta';
      title: string;
      description: string;
      index: boolean;
      noFollow: boolean;
      sitemap: boolean;
      link: {
        __typename?: 'FdrLink';
        openMethod?: Types.FdrLinkOpenMethod | null;
        legacyUrl?: string | null;
        type: Types.FdrLinkType;
        url: string;
      };
    } | null;
  };
  disrupters?: Array<{
    __typename?: 'FdrDisrupter';
    text: string;
    bgColor?: string | null;
    fgColor?: string | null;
  }> | null;
  images?: Array<{
    __typename?: 'FdrImage';
    alt: string;
    description?: string | null;
    copyright?: string | null;
    rawUrl: string;
    mediaImageResized: Array<{
      __typename?: 'FdrResizedImage';
      id: string;
      link: {
        __typename?: 'FdrLink';
        openMethod?: Types.FdrLinkOpenMethod | null;
        legacyUrl?: string | null;
        type: Types.FdrLinkType;
        url: string;
      };
    }>;
    mediaImagePreviewResized: Array<{
      __typename?: 'FdrResizedImage';
      id: string;
      link: {
        __typename?: 'FdrLink';
        openMethod?: Types.FdrLinkOpenMethod | null;
        legacyUrl?: string | null;
        type: Types.FdrLinkType;
        url: string;
      };
    }>;
    coordinates?: {
      __typename?: 'FdrCoordinates';
      latitude: number;
      longitude: number;
    } | null;
  }> | null;
  flags?: { __typename?: 'FdrHotelFlags'; extraOrdinary: boolean } | null;
  resort: {
    __typename?: 'FdrResort';
    name: string;
    destination: { __typename?: 'FdrDestination'; name: string };
  };
  requestButton: {
    __typename?: 'FdrRequestButton';
    staticCaption: string;
    objectCaption?: string | null;
    link: {
      __typename?: 'FdrLink';
      openMethod?: Types.FdrLinkOpenMethod | null;
      legacyUrl?: string | null;
      type: Types.FdrLinkType;
      url: string;
    };
  };
  boosters?: Array<{
    __typename?: 'FdrBooster';
    id: string;
    mainText: string;
    frequency?: Types.BoosterFrequency | null;
    duration: number;
    delay?: number | null;
  }> | null;
};

export const FdrHotelFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'fdrHotel' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FdrHotel' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'id' },
            name: { kind: 'Name', value: 'publicId' },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'hero' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'heroMediaImage' },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pageComponents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'pagination' },
                      value: {
                        kind: 'ObjectValue',
                        fields: [
                          {
                            kind: 'ObjectField',
                            name: { kind: 'Name', value: 'page' },
                            value: { kind: 'IntValue', value: '0' },
                          },
                          {
                            kind: 'ObjectField',
                            name: { kind: 'Name', value: 'perPage' },
                            value: { kind: 'IntValue', value: '100' },
                          },
                        ],
                      },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'fdrPageComponents' },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'b2bInformationGroups' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'fdrB2BInfoGroups' },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'webMeta' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'fdrProductWebMeta' },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'disrupters' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'fdrDisrupter' },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'images' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'mediaImage' },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'flags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'extraOrdinary' },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'leadText' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'resort' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'destination' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'requestButton' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'fdrRequestButton' },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'boosters' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'fdrBooster' },
                },
              ],
            },
          },
        ],
      },
    },
    ...HeroMediaImageFragmentDoc.definitions,
    ...FdrPageComponentsFragmentDoc.definitions,
    ...FdrB2BInfoGroupsFragmentDoc.definitions,
    ...FdrProductWebMetaFragmentDoc.definitions,
    ...FdrDisrupterFragmentDoc.definitions,
    ...MediaImageFragmentDoc.definitions,
    ...FdrRequestButtonFragmentDoc.definitions,
    ...FdrBoosterFragmentDoc.definitions,
  ],
} as unknown as DocumentNode;
